// src/pages/UserDetailPage.tsx
// Page for viewing and managing individual user

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User as UserIcon,
  Mail,
  Phone,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Ban,
  Trash2,
  Car,
  Users,
} from 'lucide-react';
import adminUserService from '../services/adminUserService';
import { User } from '../types/user';
import './UserDetailPage.css';
import Navigation from '../components/common/Navigation';


const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (userId) {
      loadUser();
    }
  }, [userId]);

  const loadUser = async () => {
    try {
      setLoading(true);
      setError('');
      const userData = await adminUserService.getUserById(userId!);
      setUser(userData);
    } catch (err: any) {
      setError(err.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    const reason = prompt('Reason for suspension:');
    if (!reason) return;

    try {
      setActionLoading(true);
      await adminUserService.suspendUser(userId!, reason);
      loadUser();
    } catch (err: any) {
      alert(err.message || 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      setActionLoading(true);
      await adminUserService.activateUser(userId!);
      loadUser();
    } catch (err: any) {
      alert(err.message || 'Failed to activate user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBan = async () => {
    const reason = prompt('Reason for ban:');
    if (!reason) return;

    const confirmed = window.confirm(
      'Are you sure you want to ban this user? This is a severe action.'
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await adminUserService.banUser(userId!, reason);
      loadUser();
    } catch (err: any) {
      alert(err.message || 'Failed to ban user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    try {
      setActionLoading(true);
      await adminUserService.verifyUser(userId!);
      loadUser();
    } catch (err: any) {
      alert(err.message || 'Failed to verify user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this user? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await adminUserService.deleteUser(userId!);
      navigate('/users');
    } catch (err: any) {
      alert(err.message || 'Failed to delete user');
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="user-detail-page loading">
        <div className="spinner"></div>
        <p>Loading user details...</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="user-detail-page error">
        <p>{error || 'User not found'}</p>
        <button onClick={() => navigate('/users')} className="btn-secondary">
          <ArrowLeft size={20} />
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <>
      <Navigation />
    <div className="user-detail-page">
      {/* Header */}
      <div className="detail-header">
        <button onClick={() => navigate('/users')} className="btn-back">
          <ArrowLeft size={20} />
          Back to Users
        </button>

        <div className="header-actions">
          {user.status === 'active' && (
            <>
              <button
                onClick={handleSuspend}
                className="btn-warning"
                disabled={actionLoading}
              >
                <UserX size={18} />
                Suspend
              </button>
              <button
                onClick={handleBan}
                className="btn-danger"
                disabled={actionLoading}
              >
                <Ban size={18} />
                Ban
              </button>
            </>
          )}

          {user.status === 'suspended' && (
            <button
              onClick={handleActivate}
              className="btn-success"
              disabled={actionLoading}
            >
              <UserCheck size={18} />
              Activate
            </button>
          )}

          {!user.isVerified && (
            <button
              onClick={handleVerify}
              className="btn-primary"
              disabled={actionLoading}
            >
              <CheckCircle size={18} />
              Verify
            </button>
          )}

          <button
            onClick={handleDelete}
            className="btn-danger-outline"
            disabled={actionLoading}
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            {user.profileImage ? (
              <img src={user.profileImage} alt={user.firstName} />
            ) : (
              <div className="avatar-placeholder-large">
                {user.firstName[0]}
                {user.lastName[0]}
              </div>
            )}
          </div>
          <div className="profile-info">
            <h1>
              {user.firstName} {user.lastName}
            </h1>
            <div className="profile-badges">
              <span className={`badge badge-${user.role}`}>
                {user.role === 'driver' && <Car size={14} />}
                {user.role === 'passenger' && <Users size={14} />}
                {user.role === 'both' && <Shield size={14} />}
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </span>
              <span className={`badge badge-${user.status}`}>
                {user.status}
              </span>
              {user.isVerified && (
                <span className="badge badge-verified">
                  <CheckCircle size={14} />
                  Verified
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="profile-details">
          <div className="detail-item">
            <Mail size={20} />
            <div>
              <label>Email</label>
              <span>{user.email}</span>
            </div>
          </div>

          <div className="detail-item">
            <Phone size={20} />
            <div>
              <label>Phone</label>
              <span>{user.phoneNumber || 'Not provided'}</span>
            </div>
          </div>

          <div className="detail-item">
            <Calendar size={20} />
            <div>
              <label>Registered</label>
              <span>
                {new Date(user.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
          </div>

          {user.lastLoginAt && (
            <div className="detail-item">
              <UserIcon size={20} />
              <div>
                <label>Last Login</label>
                <span>
                  {new Date(user.lastLoginAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Driver Profile */}
      {user.driverProfile && (
        <div className="section-card">
          <h2>
            <Car size={24} />
            Driver Profile
          </h2>
          <div className="section-content">
            <div className="info-row">
              <label>Approval Status</label>
              <span className={`badge badge-${user.driverProfile.approvalStatus}`}>
                {user.driverProfile.approvalStatus}
              </span>
            </div>
            {user.driverProfile.rating && (
              <div className="info-row">
                <label>Rating</label>
                <span>⭐ {user.driverProfile.rating.toFixed(1)}</span>
              </div>
            )}
            {user.driverProfile.totalTrips !== undefined && (
              <div className="info-row">
                <label>Total Trips</label>
                <span>{user.driverProfile.totalTrips}</span>
              </div>
            )}
            {user.driverProfile.isOnline !== undefined && (
              <div className="info-row">
                <label>Status</label>
                <span className={user.driverProfile.isOnline ? 'text-success' : 'text-muted'}>
                  {user.driverProfile.isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Passenger Profile */}
      {user.passengerProfile && (
        <div className="section-card">
          <h2>
            <Users size={24} />
            Passenger Profile
          </h2>
          <div className="section-content">
            {user.passengerProfile.rating && (
              <div className="info-row">
                <label>Rating</label>
                <span>⭐ {user.passengerProfile.rating.toFixed(1)}</span>
              </div>
            )}
            {user.passengerProfile.totalRides !== undefined && (
              <div className="info-row">
                <label>Total Rides</label>
                <span>{user.passengerProfile.totalRides}</span>
              </div>
            )}
            {user.passengerProfile.preferredPaymentMethod && (
              <div className="info-row">
                <label>Preferred Payment</label>
                <span>{user.passengerProfile.preferredPaymentMethod}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default UserDetailPage;